import { z } from 'zod'

import type { StatusTone } from '@ems/ui/patterns/status-badge'

import { api } from '#admin/lib/api-client.ts'

/** Payments and their allocations. Same money rule as invoices: format, never compute. */
export const PAYMENT_STATUSES = ['pending', 'completed', 'failed', 'expired', 'refunded'] as const
export const PAYMENT_METHODS = [
  'bank_transfer',
  'virtual_account',
  'card',
  'cash',
  'waiver',
  'other',
] as const

export const paymentSchema = z.object({
  id: z.string(),
  reference: z.string(),
  status: z.enum(PAYMENT_STATUSES),
  method: z.enum(PAYMENT_METHODS),
  currency: z.string(),
  amount: z.number(),
  allocatedAmount: z.number(),
  provider: z.string().nullable(),
  providerRef: z.string().nullable(),
  vaNumber: z.string().nullable(),
  payerName: z.string().nullable(),
  paidAt: z.string().nullable(),
  raisedForInvoiceId: z.string().nullable(),
  invoiceBillToName: z.string().nullable(),
  proofKey: z.string().nullable(),
  createdAt: z.string(),
})

const paymentDetailSchema = paymentSchema.extend({
  allocations: z.array(
    z.object({
      id: z.string(),
      invoiceId: z.string(),
      invoiceNumber: z.string().nullable(),
      billToName: z.string(),
      invoiceCurrency: z.string(),
      amount: z.number(),
      convertedAmount: z.number(),
      appliedExchangeRate: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
})

const allocationResultSchema = z.object({ paymentId: z.string() })

export type Payment = z.infer<typeof paymentSchema>
export type PaymentDetail = z.infer<typeof paymentDetailSchema>
export type PaymentStatus = Payment['status']
export type PaymentMethod = Payment['method']
/** `paidFrom` and `paidTo` are `YYYY-MM-DD` days in the edition's time zone. */
export type PaymentFilters = {
  q: string
  status: PaymentStatus | ''
  method: PaymentMethod | ''
  companyId: string
  paidFrom: string
  paidTo: string
}

const paymentPageSchema = z.object({
  items: z.array(paymentSchema),
  nextCursor: z.string().nullable(),
  total: z.number(),
})

const base = '/v1/billing/admin/payments'
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

export function fetchPayments(
  filters: PaymentFilters,
  cursor: string | undefined,
  { signal, limit = 50 }: { signal?: AbortSignal; limit?: number } = {},
) {
  const params = new URLSearchParams({ limit: String(limit) })
  for (const [key, value] of Object.entries(filters)) if (value !== '') params.set(key, value)
  if (cursor !== undefined) params.set('cursor', cursor)
  return api.get(`${base}?${params.toString()}`, {
    schema: paymentPageSchema,
    ...withSignal(signal),
  })
}

/** The companies of this edition, for the organization filter. Any signed-in operator may read it. */
export function fetchCompanies(signal?: AbortSignal) {
  return api.get('/v1/identity/companies', {
    schema: z.object({ items: z.array(z.object({ id: z.string(), name: z.string() })) }),
    ...withSignal(signal),
  })
}

export function fetchPayment(id: string, signal?: AbortSignal) {
  return api.get(`${base}/${id}`, { schema: paymentDetailSchema, ...withSignal(signal) })
}

export type PaymentBody = {
  reference: string
  method: PaymentMethod
  currency: string
  amount: string
  status: 'pending' | 'completed'
  payerName?: string
  providerRef?: string
  paidAt?: string
  invoiceId?: string
  /** The key from the `payment-proof` upload scope. */
  proofKey?: string
}

export function recordPayment(body: PaymentBody) {
  return api.post(base, { schema: paymentSchema, body })
}

export function confirmPayment(id: string, paidAt?: string) {
  return api.post(`${base}/${id}/confirm`, {
    schema: paymentSchema,
    body: paidAt === undefined ? {} : { paidAt },
  })
}

export function rejectPayment(id: string, reason: string) {
  return api.post(`${base}/${id}/reject`, { schema: paymentSchema, body: { reason } })
}

/** Attach a proof of payment to the payment, or replace its proof. */
export function attachPaymentProof(id: string, proofKey: string) {
  return api.put(`${base}/${id}/proof`, { schema: paymentSchema, body: { proofKey } })
}

/** A five-minute link to the proof of payment. Get it on click, not on load. */
export function fetchPaymentProofLink(id: string) {
  return api.get(`${base}/${id}/proof`, {
    schema: z.object({ url: z.string(), expiresInSeconds: z.number() }),
  })
}

/** `amount` is in the **payment's** currency; the server converts. */
export function allocatePayment(id: string, invoiceId: string, amount: string) {
  return api.post(`${base}/${id}/allocate`, {
    schema: allocationResultSchema,
    body: { allocations: [{ invoiceId, amount }] },
  })
}

/** What is left of a payment to allocate, from the server's two figures. */
export function unallocated(payment: Pick<Payment, 'amount' | 'allocatedAmount'>): number {
  return Math.max(payment.amount - payment.allocatedAmount, 0)
}

const METHOD_LABEL: Record<PaymentMethod, string> = {
  bank_transfer: 'Bank Transfer',
  virtual_account: 'Virtual Account',
  card: 'Card',
  cash: 'Cash',
  waiver: 'Waiver',
  other: 'Other',
}

export function methodLabel(method: PaymentMethod): string {
  return METHOD_LABEL[method]
}

/**
 * Who paid, for display. The payer's own name when there is one; otherwise the bill-to name of the
 * invoice the payment settled, flagged so the screen can say where it came from — migrated v1
 * payments carry no payer name at all.
 */
export function payerOf(
  payment: Pick<Payment, 'payerName' | 'invoiceBillToName'>,
): { name: string; fromInvoice: boolean } | null {
  if (payment.payerName !== null) return { name: payment.payerName, fromInvoice: false }
  if (payment.invoiceBillToName !== null) {
    return { name: payment.invoiceBillToName, fromInvoice: true }
  }
  return null
}

const PAYMENT_TONE: Record<PaymentStatus, StatusTone> = {
  completed: 'success',
  pending: 'warning',
  failed: 'danger',
  expired: 'neutral',
  refunded: 'info',
}

export function paymentTone(status: PaymentStatus): StatusTone {
  return PAYMENT_TONE[status]
}
